import React from "react";
import { action, flow, makeAutoObservable, observable } from "mobx";
import { api, setLogoutHandler, setTokenGetter } from "../api/client";
import { queryClient } from "../api/queryClient";

export type ServersStatus = "idle" | "loading" | "ready" | "error";

/**
 * The MobX store holds the session only: the token, the logged-in user and
 * their servers. Server data (tickets, punishments, settings...) is fetched by
 * the TanStack Query hooks in `src/api/`.
 */
export class Store {
  @observable token?: string | null = null;
  @observable user: any = null;
  @observable discord: any = null;
  @observable fastLogin: boolean = false;
  @observable servers: any[] = [];
  /** Where the list of servers is: not asked for yet, on its way, in hand, or failed. */
  @observable serversStatus: ServersStatus = "idle";
  @observable serversError: Error | null = null;
  @observable selectedServerId: string | null = null;

  constructor() {
    makeAutoObservable(this);
    const savedToken = localStorage.getItem("token");
    if (savedToken) {
      this.token = savedToken;
    }
    this.fastLogin = localStorage.getItem("fastLogin") === "true";

    // The API client sends the token and ends the session on a 401 through these.
    setTokenGetter(() => this.token);
    setLogoutHandler(() => this.logout());
  }

  /**
   * Loads the user and servers of a token saved by an earlier visit. Called once
   * from `index.tsx`, after the preview mode has started its mock server, so
   * that the first requests already go through it.
   */
  restoreSession() {
    if (!this.token) return;
    this.fetchServers();
    this.fetchUser();
    this.fetchUserDiscordData();
  }

  // --- AUTH & USERS (@me) ---
  @action
  fetchUser = flow(function* (this: Store) {
    try {
      const response = yield api.get("/users/@me");
      this.user = response.data;
    } catch (err) {
      console.error(err);
    }
  });

  @action
  fetchUserDiscordData = flow(function* (this: Store) {
    try {
      const response = yield api.get("/users/@me/discord");
      this.discord = response.data;
    } catch (err) {
      console.error(err);
    }
  });

  @action
  fetchServers = flow(function* (this: Store) {
    this.serversStatus = "loading";
    this.serversError = null;
    try {
      const response = yield api.get("/users/@me/guilds");
      this.servers = response.data;
      this.serversStatus = "ready";
    } catch (err) {
      console.error(err);
      this.serversError = err instanceof Error ? err : new Error(String(err));
      this.serversStatus = "error";
    }
  });

  /** True until the list of servers has come back, either way: there is nothing to say about it yet. */
  get serversPending(): boolean {
    return this.serversStatus === "idle" || this.serversStatus === "loading";
  }

  // --- HELPERS ---

  @action
  getManageableServers() {
    return this.servers
      .filter((s) => s.manageable)
      .sort((a, b) => (a.botAdded === b.botAdded ? 0 : a.botAdded ? -1 : 1));
  }

  @action
  currentServer() {
    return this.servers.find((s) => s.id === this.selectedServerId);
  }

  setSelectServer(id: string) {
    this.selectedServerId = id;
  }
  setToken(token: string) {
    this.token = token;
    localStorage.setItem("token", token);
  }
  logout() {
    localStorage.removeItem("token");
    this.token = null;
    this.user = null;
    this.discord = null;
    this.servers = [];
    this.serversStatus = "idle";
    this.serversError = null;
    this.selectedServerId = null;
    // What the next user logs in to must not start from this user's server data.
    queryClient.clear();
  }
}

export const store = new Store();
const StoreContext = React.createContext<Store>(store);
export const StoreProvider = ({ children }: { children: React.ReactNode }) => (
  <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
);
export const useStore = () => React.useContext(StoreContext);
